import { Autocomplete, Checkbox, IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import { MultiSelectDoneButton } from 'GeminiViewerComponent/components/MultiSelectDoneButton';
import {
    getActiveAssetActiveZone,
    getChildZones,
    selectCompactZones,
} from 'GeminiViewerComponent/features/asset/assetSlice';
import { useEffect, useMemo, useState } from 'react';
import { MdAllInclusive, MdCheckBox, MdCheckBoxOutlineBlank, MdDelete, MdFilterAlt } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { setItemLinks } from 'shared/loadZoneSlice';
import {
    getShowAllTags,
    getShowOnlyByFilter,
    getTagFilters,
    setShowAllTags,
    setShowOnlyByFilter,
    setTagFilters,
} from '../../features/globals/visibilitySlice';
import SearchFilter from '../SearchFilter';
import { searchMenuStyles } from './styles';

const SearchMenu = ({ searchItemSelected }) => {
    const dispatch = useDispatch();

    const classes = searchMenuStyles();

    const [filterBadge, setFilterBadge] = useState(0);

    const tagFilters = useSelector(getTagFilters);

    const [tagFiltersView, setTagFiltersView] = useState(tagFilters);
    const [itemTagsList, setItemTagsList] = useState([]);
    const [zoneTagsList, setZoneTagsList] = useState([]);

    const showOnlyByFilter = useSelector(getShowOnlyByFilter);
    const showAllTags = useSelector(getShowAllTags);
    const activeZone = useSelector(getActiveAssetActiveZone);
    const allZones = useSelector(selectCompactZones);
    const childZones = useSelector(getChildZones);
    const allZoneData = useMemo(() => {
        if (childZones && childZones?.length > 0) {
            return [...allZones, ...childZones];
        } else {
            return allZones;
        }
    }, [allZones, childZones]);

    const countFilters = (filters) => {
        let badgeValue = 0;
        if (filters.zone_tags.length > 0) {
            ++badgeValue;
        }
        if (filters.item_tags.length > 0) {
            ++badgeValue;
        }
        return badgeValue;
    };

    useEffect(() => {
        setTagFiltersView(tagFilters);
        setFilterBadge(countFilters(tagFilters));
    }, [tagFilters]);

    const clearFilters = () => {
        const resetTags = {
            zone_tags: [],
            item_tags: [],
        };
        dispatch(setTagFilters(resetTags));
        setTagFiltersView(resetTags);
        setFilterBadge(0);
    };

    const addFilters = () => {
        dispatch(setItemLinks({ links: [] }));
        dispatch(setTagFilters(tagFiltersView));
        setFilterBadge(countFilters(tagFiltersView));
    };

    useEffect(() => {
        let allData = [];
        let zoneTags = [];
        let uniqueIds = [];

        allZoneData.forEach((zone) => {
            if (Array.isArray(zone.zone_tags) && zone.zone_tags.length > 0) {
                allData = [...allData, ...zone.zone_tags];
            }
        });
        if (allData.length > 0) {
            allData.forEach((tag) => {
                if (!uniqueIds.includes(tag.tag_id)) {
                    zoneTags.push(tag);
                    uniqueIds.push(tag.tag_id);
                }
            });
            zoneTags.sort((tag1, tag2) => tag1.name.localeCompare(tag2.name));
            setZoneTagsList(zoneTags);
        } else {
            setZoneTagsList([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeZone]);

    useEffect(() => {
        let tagsData = [];
        let allData = [];
        let uniqueIds = [];

        if (showAllTags) {
            allZoneData.forEach((zone) => {
                if (Array.isArray(zone.item_tags) && zone.item_tags.length > 0) {
                    allData = [...allData, ...zone.item_tags];
                }
            });
            if (allData.length > 0) {
                allData.forEach((tag) => {
                    if (!uniqueIds.includes(tag.tag_id)) {
                        tagsData.push(tag);
                        uniqueIds.push(tag.tag_id);
                    }
                });
                tagsData.sort((tag1, tag2) => tag1.name.localeCompare(tag2.name));
                setItemTagsList(tagsData);
            } else {
                setItemTagsList([]);
            }
        } else {
            if (Array.isArray(activeZone?.items) && activeZone?.items?.length > 0) {
                let tagsData = [];
                let allData = [];
                let uniqueIds = [];
                activeZone?.items.forEach((item) => {
                    if (Array.isArray(item?.tags) && item?.tags.length > 0) {
                        allData = [...allData, ...item.tags];
                    }
                });
                if (allData.length > 0) {
                    allData.forEach((tag) => {
                        if (!uniqueIds.includes(tag.tag_id)) {
                            tagsData.push(tag);
                            uniqueIds.push(tag.tag_id);
                        }
                    });
                    tagsData.sort((tag1, tag2) => tag1.name.localeCompare(tag2.name));

                    setItemTagsList(tagsData);
                } else {
                    setItemTagsList([]);
                }
            } else {
                setItemTagsList([]);
            }
        }
    }, [activeZone, showAllTags]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className={classes.searchMenu}>
            <SearchFilter
                filterBadge={filterBadge}
                addFilters={addFilters}
                clearFilters={clearFilters}
                searchItemSelected={searchItemSelected}
            >
                <MenuItem id="viewer_title_search_zone-tag-container" disableRipple>
                    <Autocomplete
                        limitTags={2}
                        multiple
                        variant="outline"
                        id="viewer_title_search_zone-tag-input"
                        disableCloseOnSelect
                        options={zoneTagsList}
                        value={tagFiltersView.zone_tags}
                        clearIcon={<MdDelete fontSize="18" />}
                        onChange={(_, newInputValue) => {
                            setTagFiltersView({
                                ...tagFiltersView,
                                zone_tags: newInputValue,
                            });
                        }}
                        style={{ width: '100%' }}
                        getOptionLabel={(option) => option.name}
                        renderOption={(props, option, { selected }) => (
                            <li id={`viewer_title_search_zone-tag-item-${option.tag_id}`} {...props}>
                                <Checkbox
                                    icon={<MdCheckBoxOutlineBlank className="react-icon" />}
                                    checkedIcon={<MdCheckBox className="react-icon" />}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                                {option.name}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" label="Zone Tags" placeholder="Zone Tags" />
                        )}
                        PaperComponent={MultiSelectDoneButton}
                    />
                </MenuItem>
                <MenuItem id="viewer_title_search_item-tag-container" disableRipple>
                    <Autocomplete
                        limitTags={2}
                        multiple
                        variant="outline"
                        id="viewer_title_search_item-tag-input"
                        disableCloseOnSelect
                        options={itemTagsList}
                        value={tagFiltersView.item_tags}
                        clearIcon={<MdDelete fontSize="18" />}
                        onChange={(_, newInputValue) => {
                            setTagFiltersView({
                                ...tagFiltersView,
                                item_tags: newInputValue,
                            });
                        }}
                        isOptionEqualToValue={(option, value) => option.tag_id === value.tag_id}
                        style={{ width: '100%' }}
                        getOptionLabel={(option) => option.name}
                        renderOption={(props, option, { selected }) => (
                            <li id={`viewer_title_search_zone-tag-item-${option.tag_id}`} {...props}>
                                <Checkbox
                                    icon={<MdCheckBoxOutlineBlank className="react-icon" />}
                                    checkedIcon={<MdCheckBox className="react-icon" />}
                                    style={{
                                        marginRight: 8,
                                    }}
                                    checked={selected}
                                />
                                {option.name}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" label="Item Tags" placeholder="Item Tags" />
                        )}
                        PaperComponent={MultiSelectDoneButton}
                    />
                </MenuItem>
                <MenuItem style={{ justifyContent: 'center' }}>
                    <Tooltip title={!showOnlyByFilter === true ? 'Inclusive' : 'Exclusive'}>
                        <IconButton
                            variant="contained"
                            size="small"
                            id="viewer_title_search_toggle-inclusive-exclusive-btn"
                            onClick={() => {
                                dispatch(setShowOnlyByFilter());
                            }}
                            style={{
                                color: `${!showOnlyByFilter ? '#CCCCCC' : '#000000'}`,
                            }}
                        >
                            <MdFilterAlt className="react-icon" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={`${showAllTags ? 'Zone' : 'All'} Tags`}>
                        <IconButton
                            variant="contained"
                            size="small"
                            id="viewer_title_search_toggle-inclusive-exclusive-btn"
                            onClick={() => {
                                dispatch(setShowAllTags());
                            }}
                            style={{
                                color: `${!showAllTags ? '#CCCCCC' : '#000000'}`,
                            }}
                        >
                            <MdAllInclusive className="react-icon" />
                        </IconButton>
                    </Tooltip>
                </MenuItem>
            </SearchFilter>
        </div>
    );
};

export { SearchMenu };
